import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  CreatePettyCashClose,
  CreatePettyCashCount,
  CreatePettyCashEntry,
  CreatePettyCashFloat,
  CreatePettyCashReplenishment,
  PettyCashAttachment,
  PettyCashClose,
  PettyCashCount,
  PettyCashEntry,
  PettyCashEntryStatus,
  PettyCashFloatListRow,
  PettyCashFloatStatus,
  PettyCashReplenishmentWithEntries,
} from '@dcc-erp/shared';

import { api, apiJson, withSodOverride } from '../api';

// Cache-key scopes — keyed by companyId so DCC/TTOP/RCI switches give
// independent caches (same pattern as ar.ts).
const floatsKey = (companyId: string | undefined) => ['pc-floats', companyId] as const;
const entriesKey = (companyId: string | undefined) => ['pc-entries', companyId] as const;
const replenishmentsKey = (companyId: string | undefined) =>
  ['pc-replenishments', companyId] as const;
const countsKey = (companyId: string | undefined) => ['pc-counts', companyId] as const;
const closesKey = (companyId: string | undefined) => ['pc-closes', companyId] as const;

// ============================================================
// Floats
// ============================================================

export interface PettyCashFloatFilters {
  branchId?: string;
  status?: PettyCashFloatStatus;
}

export function usePettyCashFloats(
  companyId: string | undefined,
  filters: PettyCashFloatFilters = {},
) {
  const qs = new URLSearchParams();
  if (filters.branchId) qs.set('branchId', filters.branchId);
  if (filters.status) qs.set('status', filters.status);
  const search = qs.toString();
  return useQuery({
    queryKey: [...floatsKey(companyId), filters],
    queryFn: () =>
      api<PettyCashFloatListRow[]>(
        `/companies/${companyId}/pc/floats${search ? `?${search}` : ''}`,
      ),
    enabled: !!companyId,
  });
}

export function useCreatePettyCashFloat(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      data,
      idempotencyKey,
    }: {
      data: CreatePettyCashFloat;
      idempotencyKey?: string;
    }) =>
      api<PettyCashFloatListRow>(`/companies/${companyId}/pc/floats`, {
        method: 'POST',
        headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: floatsKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
    },
  });
}

// ============================================================
// Entries
// ============================================================

export interface PettyCashEntryFilters {
  floatId?: string;
  dateFrom?: string;
  dateTo?: string;
  status?: PettyCashEntryStatus;
  categoryCode?: string;
}

export function usePettyCashEntries(
  companyId: string | undefined,
  filters: PettyCashEntryFilters = {},
) {
  const qs = new URLSearchParams();
  if (filters.floatId) qs.set('floatId', filters.floatId);
  if (filters.dateFrom) qs.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) qs.set('dateTo', filters.dateTo);
  if (filters.status) qs.set('status', filters.status);
  if (filters.categoryCode) qs.set('categoryCode', filters.categoryCode);
  const search = qs.toString();
  return useQuery({
    queryKey: [...entriesKey(companyId), filters],
    queryFn: () =>
      api<PettyCashEntry[]>(`/companies/${companyId}/pc/entries${search ? `?${search}` : ''}`),
    enabled: !!companyId,
  });
}

export function useCreatePettyCashEntry(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      data,
      idempotencyKey,
    }: {
      data: CreatePettyCashEntry;
      idempotencyKey?: string;
    }) =>
      api<PettyCashEntry>(`/companies/${companyId}/pc/entries`, {
        method: 'POST',
        headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      // Entry creation mutates float.currentBalance, so the floats list is also
      // stale. Invalidate both plus the JE list.
      void qc.invalidateQueries({ queryKey: entriesKey(companyId) });
      void qc.invalidateQueries({ queryKey: floatsKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
    },
  });
}

// ------------------------------------------------------------
// Entry approval (maker-checker via the central approval engine)
// ------------------------------------------------------------

// Final-step approval auto-posts the deferred JV, flips the entry to POSTED and
// decrements the float, so the floats list + JE list are also stale.
function invalidatePettyCashEngine(
  qc: ReturnType<typeof useQueryClient>,
  companyId: string | undefined,
) {
  void qc.invalidateQueries({ queryKey: entriesKey(companyId) });
  void qc.invalidateQueries({ queryKey: floatsKey(companyId) });
  void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
}

// Submit a DRAFT / RETURNED entry into the engine chain.
export function useSubmitPettyCashEntry(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entryId: string) =>
      apiJson<undefined, PettyCashEntry>(
        'POST',
        `/companies/${companyId}/pc/entries/${entryId}/submit`,
      ),
    onSuccess: () => invalidatePettyCashEngine(qc, companyId),
  });
}

// Approve the current engine step (final-step approval auto-posts the entry JV).
export function useApprovePettyCashEntryStep(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      entryId,
      step,
      comment,
      sodOverrideReason,
    }: {
      entryId: string;
      step: number;
      comment?: string;
      sodOverrideReason?: string;
    }) =>
      apiJson<{ step: number; comment?: string; sodOverrideReason?: string }, PettyCashEntry>(
        'POST',
        `/companies/${companyId}/pc/entries/${entryId}/approve-step`,
        withSodOverride({ step, ...(comment !== undefined ? { comment } : {}) }, sodOverrideReason),
      ),
    onSuccess: () => invalidatePettyCashEngine(qc, companyId),
  });
}

// Reject the approval at a step (terminal).
export function useRejectPettyCashEntryStep(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      entryId,
      step,
      reason,
      sodOverrideReason,
    }: {
      entryId: string;
      step: number;
      reason: string;
      sodOverrideReason?: string;
    }) =>
      apiJson<{ step: number; reason: string; sodOverrideReason?: string }, PettyCashEntry>(
        'POST',
        `/companies/${companyId}/pc/entries/${entryId}/reject-step`,
        withSodOverride({ step, reason }, sodOverrideReason),
      ),
    onSuccess: () => invalidatePettyCashEngine(qc, companyId),
  });
}

// Return a PENDING entry for revision (RETURNED — resubmittable).
export function useReturnPettyCashEntry(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      entryId,
      step,
      reason,
      sodOverrideReason,
    }: {
      entryId: string;
      step: number;
      reason: string;
      sodOverrideReason?: string;
    }) =>
      apiJson<{ step: number; reason: string; sodOverrideReason?: string }, PettyCashEntry>(
        'POST',
        `/companies/${companyId}/pc/entries/${entryId}/return`,
        withSodOverride({ step, reason }, sodOverrideReason),
      ),
    onSuccess: () => invalidatePettyCashEngine(qc, companyId),
  });
}

// ------------------------------------------------------------
// Entry VAT realization (SUSPENSE → CLAIMABLE)
// ------------------------------------------------------------

// Realize a SUSPENSE entry's input VAT once the tax invoice clears: posts the
// suspense→claimable JV, so the entries list + JE list are stale (the float
// balance is untouched — the gross was already deducted at entry posting).
export function useRealizePettyCashEntryVat(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entryId: string) =>
      apiJson<undefined, PettyCashEntry>(
        'POST',
        `/companies/${companyId}/pc/entries/${entryId}/realize-vat`,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: entriesKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
    },
  });
}

// ============================================================
// Replenishments
// ============================================================

export interface PettyCashReplenishmentFilters {
  floatId?: string;
}

export function usePettyCashReplenishments(
  companyId: string | undefined,
  filters: PettyCashReplenishmentFilters = {},
) {
  const qs = new URLSearchParams();
  if (filters.floatId) qs.set('floatId', filters.floatId);
  const search = qs.toString();
  return useQuery({
    queryKey: [...replenishmentsKey(companyId), filters],
    queryFn: () =>
      api<PettyCashReplenishmentWithEntries[]>(
        `/companies/${companyId}/pc/replenishments${search ? `?${search}` : ''}`,
      ),
    enabled: !!companyId,
  });
}

export function useCreatePettyCashReplenishment(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      data,
      idempotencyKey,
    }: {
      data: CreatePettyCashReplenishment;
      idempotencyKey?: string;
    }) =>
      api<PettyCashReplenishmentWithEntries>(`/companies/${companyId}/pc/replenishments`, {
        method: 'POST',
        headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      // Replenishment flips entries to REPLENISHED, bumps float balance, and
      // posts a JV — invalidate all three caches.
      void qc.invalidateQueries({ queryKey: replenishmentsKey(companyId) });
      void qc.invalidateQueries({ queryKey: entriesKey(companyId) });
      void qc.invalidateQueries({ queryKey: floatsKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
    },
  });
}

// ============================================================
// Cash counts (PCC) — periodic count → over/short JV via the engine
// ============================================================

// A count creation + final-step approval posts an over/short JV and (on the
// expected = float.currentBalance basis) reconciles against the float, so the
// counts list + floats + JE list are all stale on any engine transition.
function invalidatePettyCashCount(
  qc: ReturnType<typeof useQueryClient>,
  companyId: string | undefined,
) {
  void qc.invalidateQueries({ queryKey: countsKey(companyId) });
  void qc.invalidateQueries({ queryKey: floatsKey(companyId) });
  void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
}

export function usePettyCashCounts(companyId: string | undefined, floatId?: string) {
  const qs = new URLSearchParams();
  if (floatId) qs.set('floatId', floatId);
  const search = qs.toString();
  return useQuery({
    queryKey: [...countsKey(companyId), floatId ?? null],
    queryFn: () =>
      api<PettyCashCount[]>(`/companies/${companyId}/pc/counts${search ? `?${search}` : ''}`),
    enabled: !!companyId,
  });
}

export function useCreatePettyCashCount(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      data,
      idempotencyKey,
    }: {
      data: CreatePettyCashCount;
      idempotencyKey?: string;
    }) =>
      api<PettyCashCount>(`/companies/${companyId}/pc/counts`, {
        method: 'POST',
        headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
        body: JSON.stringify(data),
      }),
    onSuccess: () => invalidatePettyCashCount(qc, companyId),
  });
}

// Submit a DRAFT / RETURNED count into the engine chain.
export function useSubmitPettyCashCount(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (countId: string) =>
      apiJson<undefined, PettyCashCount>(
        'POST',
        `/companies/${companyId}/pc/counts/${countId}/submit`,
      ),
    onSuccess: () => invalidatePettyCashCount(qc, companyId),
  });
}

// Approve the current engine step (final-step approval auto-posts the over/short JV).
export function useApprovePettyCashCountStep(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      countId,
      step,
      comment,
      sodOverrideReason,
    }: {
      countId: string;
      step: number;
      comment?: string;
      sodOverrideReason?: string;
    }) =>
      apiJson<{ step: number; comment?: string; sodOverrideReason?: string }, PettyCashCount>(
        'POST',
        `/companies/${companyId}/pc/counts/${countId}/approve-step`,
        withSodOverride({ step, ...(comment !== undefined ? { comment } : {}) }, sodOverrideReason),
      ),
    onSuccess: () => invalidatePettyCashCount(qc, companyId),
  });
}

// Reject the approval at a step (terminal).
export function useRejectPettyCashCountStep(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      countId,
      step,
      reason,
      sodOverrideReason,
    }: {
      countId: string;
      step: number;
      reason: string;
      sodOverrideReason?: string;
    }) =>
      apiJson<{ step: number; reason: string; sodOverrideReason?: string }, PettyCashCount>(
        'POST',
        `/companies/${companyId}/pc/counts/${countId}/reject-step`,
        withSodOverride({ step, reason }, sodOverrideReason),
      ),
    onSuccess: () => invalidatePettyCashCount(qc, companyId),
  });
}

// Return a PENDING count for revision (RETURNED — resubmittable).
export function useReturnPettyCashCount(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      countId,
      step,
      reason,
      sodOverrideReason,
    }: {
      countId: string;
      step: number;
      reason: string;
      sodOverrideReason?: string;
    }) =>
      apiJson<{ step: number; reason: string; sodOverrideReason?: string }, PettyCashCount>(
        'POST',
        `/companies/${companyId}/pc/counts/${countId}/return`,
        withSodOverride({ step, reason }, sodOverrideReason),
      ),
    onSuccess: () => invalidatePettyCashCount(qc, companyId),
  });
}

// ============================================================
// Float closes (PCX) — return residual cash to a bank account via the engine
// ============================================================

// A close creation + final-step approval posts the return JV and marks the
// float CLOSED, so the closes list + floats + JE list are all stale on any
// engine transition.
function invalidatePettyCashClose(
  qc: ReturnType<typeof useQueryClient>,
  companyId: string | undefined,
) {
  void qc.invalidateQueries({ queryKey: closesKey(companyId) });
  void qc.invalidateQueries({ queryKey: floatsKey(companyId) });
  void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
}

export function usePettyCashCloses(companyId: string | undefined, floatId?: string) {
  const qs = new URLSearchParams();
  if (floatId) qs.set('floatId', floatId);
  const search = qs.toString();
  return useQuery({
    queryKey: [...closesKey(companyId), floatId ?? null],
    queryFn: () =>
      api<PettyCashClose[]>(`/companies/${companyId}/pc/closes${search ? `?${search}` : ''}`),
    enabled: !!companyId,
  });
}

export function useCreatePettyCashClose(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      data,
      idempotencyKey,
    }: {
      data: CreatePettyCashClose;
      idempotencyKey?: string;
    }) =>
      api<PettyCashClose>(`/companies/${companyId}/pc/closes`, {
        method: 'POST',
        headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
        body: JSON.stringify(data),
      }),
    onSuccess: () => invalidatePettyCashClose(qc, companyId),
  });
}

// Submit a DRAFT / RETURNED close into the engine chain.
export function useSubmitPettyCashClose(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (closeId: string) =>
      apiJson<undefined, PettyCashClose>(
        'POST',
        `/companies/${companyId}/pc/closes/${closeId}/submit`,
      ),
    onSuccess: () => invalidatePettyCashClose(qc, companyId),
  });
}

// Approve the current engine step (final-step approval auto-posts the return JV
// and marks the float CLOSED).
export function useApprovePettyCashCloseStep(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      closeId,
      step,
      comment,
      sodOverrideReason,
    }: {
      closeId: string;
      step: number;
      comment?: string;
      sodOverrideReason?: string;
    }) =>
      apiJson<{ step: number; comment?: string; sodOverrideReason?: string }, PettyCashClose>(
        'POST',
        `/companies/${companyId}/pc/closes/${closeId}/approve-step`,
        withSodOverride({ step, ...(comment !== undefined ? { comment } : {}) }, sodOverrideReason),
      ),
    onSuccess: () => invalidatePettyCashClose(qc, companyId),
  });
}

// Reject the approval at a step (terminal).
export function useRejectPettyCashCloseStep(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      closeId,
      step,
      reason,
      sodOverrideReason,
    }: {
      closeId: string;
      step: number;
      reason: string;
      sodOverrideReason?: string;
    }) =>
      apiJson<{ step: number; reason: string; sodOverrideReason?: string }, PettyCashClose>(
        'POST',
        `/companies/${companyId}/pc/closes/${closeId}/reject-step`,
        withSodOverride({ step, reason }, sodOverrideReason),
      ),
    onSuccess: () => invalidatePettyCashClose(qc, companyId),
  });
}

// Return a PENDING close for revision (RETURNED — resubmittable).
export function useReturnPettyCashClose(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      closeId,
      step,
      reason,
      sodOverrideReason,
    }: {
      closeId: string;
      step: number;
      reason: string;
      sodOverrideReason?: string;
    }) =>
      apiJson<{ step: number; reason: string; sodOverrideReason?: string }, PettyCashClose>(
        'POST',
        `/companies/${companyId}/pc/closes/${closeId}/return`,
        withSodOverride({ step, reason }, sodOverrideReason),
      ),
    onSuccess: () => invalidatePettyCashClose(qc, companyId),
  });
}

// ============================================================
// Attachments
// ============================================================

// POST /companies/:companyId/pc/attachments (multipart) — stage a receipt file.
// Do NOT set Content-Type: the browser sets the multipart boundary for FormData.
// Returns the attachmentId to embed in CreatePettyCashEntry.attachmentId.
export function useStagePettyCashReceipt(companyId: string | undefined) {
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file, file.name);
      return api<{ attachmentId: string }>(`/companies/${companyId}/pc/attachments`, {
        method: 'POST',
        body: form,
      });
    },
  });
}

// GET /companies/:companyId/pc/entries/:entryId/attachments — list attachments
// for a single entry, each row includes a presigned downloadUrl.
export function usePettyCashEntryAttachments(
  companyId: string | undefined,
  entryId: string | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['pc-attachments', companyId, entryId],
    queryFn: () =>
      api<PettyCashAttachment[]>(`/companies/${companyId}/pc/entries/${entryId}/attachments`),
    // Default enabled (auto-fetch); callers like the lazy "view receipt" link
    // pass { enabled: false } and trigger a fresh fetch via refetch() on click
    // (the downloadUrl is a short-lived presigned URL — fetch on demand, not per row).
    enabled: (options?.enabled ?? true) && !!companyId && !!entryId,
  });
}
